import {readStoredAuth} from '../api/authApi';
import {getVisitaEditContext,getVisitaContextRevisions,downloadVisitaOfflinePhoto,downloadVisitaOfflineDocument} from '../api/clients/visitasClient';
import {getNetworkOnlineStatus} from '../mobile/networkService';
import {createVisitaOfflineContext} from './visitaOfflineContext';
import {migrateLegacyVisitaContexts} from './visitaContextLegacy';
import {getVisitaOfflineEntry} from './visitaOfflineQueue';
import {getVisitaOfflineOperationsByVisita} from './visitaOfflineOperationQueue';
import {getOfflineImageEntriesByVisita} from './visitaOfflineMediaQueue';
import {withVisitaLocalLock} from './visitaLocalFirst';
import {getVisitaFormEditor,emitVisitaFormUpdate,markVisitaFormUpdate,getFormBlockedVisits,formUpdateCompatibility} from './visitaFormUpdatePolicy';
import {visitaOfflineContextScope,visitaOfflineContextKey} from './visitaOfflineContext';
import {createVisitaFormUpdater} from './visitaFormUpdatesCoordinator';
import {Filesystem} from '@capacitor/filesystem';
import {OFFLINE_STORES,getOfflineStorageDriver} from './offlineStorageAdapter';
import {createOfflineFileVault} from './offlineFileVault';
import {projectAcknowledgedAttachment} from './visitaAttachmentProjection';
import {projectPendingVisitaStart} from './visitaStartIntent';
import {prepareClosedVisitaFiles} from './visitaClosedFilePreparation';
import {createVisitaPhotoDependencies} from './visitaOfflinePhotoDependencies';
import {createVisitaPdfDependencies,combineVisitaFileDependencies} from './visitaOfflinePdfDependencies';
import {inspectOfflinePdf} from '../mobile/offlinePdfReader';

export async function canPrepareVisitaReadFiles(user, visitaId) {
    const [draft,operations,photos]=await Promise.all([getVisitaOfflineEntry({userId:user.id,visitaId}),getVisitaOfflineOperationsByVisita(visitaId,user.id),getOfflineImageEntriesByVisita(visitaId,user.id)]);
    // Un cierre congelado en conflicto necesita poder leer la versión autorizada
    // para conciliar. No contiene ediciones que esta descarga pueda reemplazar:
    // su payload/firma permanecen en la outbox hasta una confirmación explícita.
    const blocks=operations.some(item=>item.status!=='applied'&&!(item.type==='finalizar_visita'&&item.status==='conflict'));
    return !draft&&!blocks&&!photos.length;
}

export async function canApplyVisitaForm(user, scope, visitaId) {
    if(getVisitaFormEditor(scope,visitaId)?.blocked())return false;
    return await canPrepareVisitaReadFiles(user,visitaId)&&!getVisitaFormEditor(scope,visitaId)?.blocked();
}

export function createVisitaContextClient({user,isCurrent=()=>true,executionToken=null}){
    const client=createVisitaOfflineContext({user,isCurrent,sessionProvider:readStoredAuth,isOnline:getNetworkOnlineStatus,
        canApply:id=>canApplyVisitaForm(user,client.scope,id),withLocalLock:(id,task)=>withVisitaLocalLock(user.id,id,task),
        onApplied:(id,context,previous)=>getVisitaFormEditor(client.scope,id)?.apply(context,previous)??true,
        fetchContext:(id,session)=>getVisitaEditContext(executionToken||session.token,id,{forOffline:true}),migrate:migrateLegacyVisitaContexts});
    const read=client.read;
    client.read=async id=>projectPendingVisitaStart(await read(id),await getVisitaOfflineOperationsByVisita(id,user.id),user);
    const load=client.load;
    client.load=async (id,{onReadOnlyContext}={})=>{
        const result=await load(id);
        // El contexto del servidor ya pasó autorización y persistencia. Mostrarlo
        // no confirma archivos: su conciliación y lectura conservan las barreras.
        if(!executionToken&&typeof onReadOnlyContext==='function'&&isCurrent()&&result.source==='server'
            &&!result.reason&&!result.storageError
            &&['finalizada','revisada','aprobada'].includes(result.context?.visita?.estado))
            await onReadOnlyContext(result);
        const driver=getOfflineStorageDriver(),vault=createOfflineFileVault({fs:Filesystem,driver});
        const files=await driver.getAll(OFFLINE_STORES.FILES,null,{userId:Number(user.id)});
        for(const file of files)if(file.userId===Number(user.id)&&file.empresaId===Number(user.empresa_id)
            &&file.visitaId===Number(id)&&file.state==='acknowledged'&&['image','document'].includes(file.kind))
            await projectAcknowledgedAttachment(file,{driver,verifyBytes:vault.verify,prepareReadCopy:vault.prepareReadCopy,isCurrent});
        const context=result.source==='server'&&result.storageError?null:await client.read(id);
        const projected=context?{...result,context}:result;
        if(executionToken)return projected; // La orden remota no amplía su alcance descargando adjuntos.
        const source=await driver.get(OFFLINE_STORES.CONTEXTS,visitaOfflineContextKey(user,id,client.scope));
        const filePreparation=await prepareClosedVisitaFiles({result:projected,source,user,isCurrent,
            // Copiar bytes autorizados no sustituye respuestas ni retira pendientes.
            // El manifiesto revalida snapshot/sesión y conserva todos los journals.
            canPrepare:()=>canPrepareVisitaReadFiles(user,id),
            prepareFiles:async(source,dependencies)=>{
                const session=await readStoredAuth();
                if(!isCurrent()||visitaOfflineContextScope(session?.user)!==client.scope||!session?.token)throw Error('Cambió la sesión de los archivos.');
                return combineVisitaFileDependencies(
                    createVisitaPhotoDependencies({user,fs:Filesystem,driver,isCurrent,download:(visit,item)=>downloadVisitaOfflinePhoto(session.token,visit,item)}),
                    createVisitaPdfDependencies({user,fs:Filesystem,driver,isCurrent,download:(visit,item)=>downloadVisitaOfflineDocument(session.token,visit,item),validateFile:inspectOfflinePdf})
                ).prepare(source,dependencies);
            }});
        const prepared={...projected,filePreparation};
        if(import.meta.env?.VITE_LOCAL_ACCEPTANCE_DIAGNOSTICS==='true'&&['finalizada','revisada','aprobada'].includes(projected.context?.visita?.estado))
            console.debug('[VisitaClosedFiles]',JSON.stringify({visit:Number(id),source:result.source,
                contextReason:result.reason||null,storageError:Boolean(result.storageError),contextKind:source?.kind||null,
                scopeMatches:source?.scope===client.scope,userMatches:source?.userId===Number(user.id),
                companyMatches:source?.empresaId===Number(user.empresa_id),...filePreparation}));
        markVisitaFormUpdate(client.scope,id,prepared);return prepared;
    };
    return client;
}

export async function getBlockedVisitaForms(user) {
    const scope=visitaOfflineContextScope(user);
    if(!scope)return new Set();
    const blocked=getFormBlockedVisits(scope),client=createVisitaContextClient({user});
    try{
        const rows=await client.prepared();
        for(const row of rows){
            if(!row.candidate){if(row.hint)blocked.add(row.visitaId);else blocked.delete(row.visitaId);continue;}
            const [current,next]=await Promise.all([client.read(row.visitaId),client.candidate(row.visitaId)]);
            // Aunque el snapshot aún no tenga respuestas, puede haberlas en la cola.
            // Una reordenación no debe enviar esas keys sobre la estructura nueva.
            if(row.hint&&row.hint.revision!==row.candidate.revision||!next||!current||!formUpdateCompatibility(current,next.context).preserved)blocked.add(row.visitaId);
            else blocked.delete(row.visitaId);
        }
        return blocked;
    }finally{client.dispose();}
}

export async function checkVisitaFormNow(user, token, visitaId, {requireReady=false}={}) {
    const scope=visitaOfflineContextScope(user);
    if(!scope)return {notEnabled:true};
    if(!await getNetworkOnlineStatus())return {offline:true};
    let disposed=false;
    const client=createVisitaContextClient({user,isCurrent:()=>!disposed});
    const updater=createVisitaFormUpdater({user,client,isAvailable:()=>!disposed,fetchRevisions:ids=>getVisitaContextRevisions(token,ids),publish:(id,result)=>emitVisitaFormUpdate(scope,id,result)});
    try{
        const results=await updater.check([Number(visitaId)],{includeNew:true,force:true});
        const result=results[0];
        if(requireReady&&(!result||result.error||result.reason==='form_update_pending'))throw Error('Hay una actualización del formulario pendiente. Revisá los cambios y la sincronización antes de iniciar.');
        return result;
    }finally{disposed=true;updater.dispose();}
}
